import { AfterViewInit, Component, ElementRef, ViewChild, input, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OriTooltip, OriTooltipContent } from '~/components/ui/tooltip';
import { RdxTooltipContentDirective, RdxTooltipTriggerDirective } from '@radix-ng/primitives/tooltip';

@Component({
  standalone: true,
  imports: [CommonModule, OriTooltip, OriTooltipContent, RdxTooltipTriggerDirective, RdxTooltipContentDirective],
  template: `
    <!-- If text is truncated, show a rich tooltip; otherwise just render text -->
    <ng-container *ngIf="isTruncated; else plain" oriTooltip [openDelay]="200">
      <div #textEl rdxTooltipTrigger class="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
        {{ text }}
      </div>
      <ng-template rdxTooltipContent>
        <ori-tooltip-content class="dark px-2 py-1 text-xs max-w-[480px] whitespace-normal break-words">
          {{ text }}
        </ori-tooltip-content>
      </ng-template>
    </ng-container>
    <ng-template #plain>
      <div #textEl class="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
        {{ text }}
      </div>
    </ng-template>
  `,
})
export class TruncatedTextTooltip implements AfterViewInit, OnDestroy {
  readonly value = input<unknown>('');

  @ViewChild('textEl', { static: false }) textEl!: ElementRef<HTMLElement>;
  isTruncated = false;
  private ro?: ResizeObserver;

  get text(): string {
    const v = this.value();
    return v == null ? '' : String(v);
  }

  ngAfterViewInit(): void {
    // Defer to ensure layout is complete and widths are correct
    setTimeout(() => {
      this.updateTruncation();
      const el = this.textEl?.nativeElement;
      if (!el) return;
      this.ro = new ResizeObserver(() => this.updateTruncation());
      this.ro.observe(el);
    });
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
  }

  @HostListener('window:resize')
  onWinResize() {
    this.updateTruncation();
  }

  private updateTruncation() {
    const el = this.textEl?.nativeElement;
    if (!el) return;
    // Slight tolerance to avoid off-by-one flicker
    const truncated = el.scrollWidth - el.clientWidth > 1;
    this.isTruncated = truncated;
  }
}
